/* ─────────────────────────────────────────────────────────────────────────────
   Marketing / Funnel — the sections below the KPI grid, replicating the original
   GrowthOS page (frontend/src/app/(dashboard)/analytics/marketing/page.tsx):
   Pipeline scoreboard (Pipeline → Sales POC → deals), D2C Freight four-stage funnel,
   Platform performance (+ MoM and consolidated source drill), Sales POC-wise
   scoreboard (brand drill), Closed Won Deals, Sales POC & pipeline-wise closure %.
   Data comes from the reference-shaped sections of /growthos-admin/marketing-funnel.
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import BarChartIcon from '@mui/icons-material/BarChart'
import GroupsIcon from '@mui/icons-material/Groups'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import PercentIcon from '@mui/icons-material/Percent'
import { GX_BORDER, GX_CARD_SX, GX_COLORS, GX_INK_500, GX_INK_700, GX_INK_900 } from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'
import {
  ABM_LEAD_BASIS, compactCurrency, currency, integer, isAbmSource, monthLabel, percentage, shortDate,
} from './mfFormat'

const MfDataTable = (props) => <GxPagedTable variant="marketing" {...props} />

const INK_400 = '#94a3b8'
const stripPipeline = (label) => String(label || '').replace(' Pipeline', '')

// ── small primitives ─────────────────────────────────────────────────────────
export function Chevron({ open, size = 16, color }) {
  return <ExpandMoreIcon sx={{ fontSize: size, color, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }} />
}

export function ScopeTag({ label, title }) {
  return (
    <Tooltip title={title || ''} arrow>
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '9999px',
        border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', fontSize: 11, fontWeight: 600, color: GX_INK_500, whiteSpace: 'nowrap',
      }}>{label}</Box>
    </Tooltip>
  )
}

export function SectionTitle({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon && <Box sx={{ color: GX_COLORS.indigo, display: 'flex' }}>{icon}</Box>}
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: GX_INK_900 }}>{children}</Typography>
    </Box>
  )
}

export function Card({ children, sx }) {
  return <Box sx={{ ...GX_CARD_SX, border: `1px solid ${GX_BORDER}`, mb: 3, overflow: 'hidden', ...sx }}>{children}</Box>
}

export function ToggleBar({ open, onClick, children, sx }) {
  return (
    <Box component="button" onClick={onClick} aria-expanded={open} sx={{
      display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left', cursor: 'pointer',
      borderRadius: '12px', border: '1px solid rgba(79,70,229,0.18)', px: 2, py: 1.5, fontSize: 13,
      background: 'linear-gradient(90deg, rgba(79,70,229,0.07), rgba(6,182,212,0.05))', color: GX_INK_700,
      '&:hover': { borderColor: 'rgba(79,70,229,0.32)', background: 'linear-gradient(90deg, rgba(79,70,229,0.11), rgba(6,182,212,0.08))' },
      ...sx,
    }}>
      <Chevron open={open} />
      {children}
    </Box>
  )
}

function TeamBadge({ team }) {
  if (!team) return null
  return (
    <Box component="span" sx={{ ml: 0.75, px: 1, py: 0.125, borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, fontSize: 10.5, fontWeight: 600, color: GX_INK_700, bgcolor: '#fff' }}>
      {team}
    </Box>
  )
}

function DrillName({ open, name, team, bold }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#4338ca', fontWeight: bold ? 700 : 500 }}>
      <Chevron open={open} size={15} /> {name}<TeamBadge team={team} />
    </Box>
  )
}

function StatusBadge({ status }) {
  const tone = status === 'Won' ? { bg: '#dcfce7', fg: '#166534' } : status === 'Lost' ? { bg: '#ffe4e6', fg: '#9f1239' } : { bg: '#fef3c7', fg: '#92400e' }
  return <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: '9999px', fontSize: 11, fontWeight: 600, bgcolor: tone.bg, color: tone.fg }}>{status}</Box>
}

function StatusPill({ status }) {
  const live = status === 'Live'
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: '9999px', fontSize: 11, fontWeight: 600,
      bgcolor: live ? 'rgba(16,185,129,0.12)' : 'rgba(244,63,94,0.12)', color: live ? '#047857' : '#be123c',
    }}>{status}</Box>
  )
}

function Tile3d({ label, value, sub, subTitle, labelTitle }) {
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', p: 1.5, background: 'linear-gradient(160deg,#ffffff 0%,#f8fafc 100%)', boxShadow: '0 6px 14px -9px rgba(15,23,42,0.16)' }}>
      <Typography title={labelTitle} sx={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: GX_INK_900, mt: 0.25 }}>{value}</Typography>
      {sub && <Typography title={subTitle} sx={{ fontSize: 12, color: INK_400 }}>{sub}</Typography>}
    </Box>
  )
}

export function ChartTip({ active, payload, label, moneyKeys = [] }) {
  if (!active || !payload?.length) return null
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', bgcolor: '#fff', p: 1.5, fontSize: 12, boxShadow: '0 10px 20px rgba(15,23,42,0.12)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>{label}</Typography>
      {payload.map((item) => (
        <Box key={item.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2.5, minWidth: 160, py: 0.25 }}>
          <span style={{ color: item.color }}>{item.name}</span>
          <strong>{moneyKeys.includes(item.dataKey) ? currency(item.value) : integer(item.value)}</strong>
        </Box>
      ))}
    </Box>
  )
}

const drillBox = (bg = '#f8fafc') => ({ bgcolor: bg, p: 1.5 })
const wrapSx = { whiteSpace: 'normal' }

// Shared deal-level drill columns (dealDrillRow in the reference).
const dealColumns = (dateTitle = 'Deal create date (HubSpot).') => [
  { key: 'status', header: 'Status', title: 'Deal outcome: Won, Lost, or Open (undecided).', sortValue: (d) => d.status, cell: (d) => <StatusBadge status={d.status} /> },
  { key: 'brand', header: 'Brand Name', title: 'Brand on the deal.', sortValue: (d) => d.brandName, cell: (d) => <strong>{d.brandName}</strong> },
  { key: 'website', header: 'Website', title: 'Brand website captured on the deal.', sortValue: (d) => d.website ?? '', cell: (d) => d.website ?? '—' },
  { key: 'date', header: 'Date', title: dateTitle, sortValue: (d) => d.date ?? '', cell: (d) => d.date ?? '—' },
  { key: 'category', header: 'Product Category', title: 'Product category captured on the deal.', sortValue: (d) => d.productCategory ?? '', cell: (d) => d.productCategory ?? '—' },
  { key: 'dailyOrders', header: 'Daily Orders', title: 'Expected daily orders on the deal.', sortValue: (d) => d.dailyOrders, cell: (d) => integer(d.dailyOrders) },
  { key: 'monthlyOrders', header: 'Est. Monthly Orders', title: 'Expected monthly orders (shipment potential).', sortValue: (d) => d.estimatedMonthlyOrders, cell: (d) => integer(d.estimatedMonthlyOrders) },
  { key: 'monthlyMbr', header: 'Est. Monthly MBR', title: 'Estimated Monthly Billing Revenue (₹/month).', sortValue: (d) => d.estimatedMonthlyMbr, cell: (d) => currency(d.estimatedMonthlyMbr) },
]

const OPP_CLOSED_GROUPS = [
  { label: '', span: 6 },
  { label: 'Opportunities', span: 2, variant: 'a', title: 'Deals created in the window.' },
  { label: 'Customers · Closed', span: 2, variant: 'b', title: 'Deals won with a CLOSE date in the window, any create date.' },
]

// ── Pipeline scoreboard: chart + Pipeline → POC → deals (3-level clickable) ──
export function PipelineScoreboard({ pipelines, windowLabel }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [openPipe, setOpenPipe] = useState(null)
  const [openPoc, setOpenPoc] = useState(null)
  const chart = (pipelines || []).map((p) => ({ name: stripPipeline(p.pipelineLabel), opportunities: p.opportunities, customersClosed: p.customersClosed }))
  return (
    <Card>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<BarChartIcon sx={{ fontSize: 18 }} />}>Pipeline scoreboard</SectionTitle>
          {windowLabel && <ScopeTag label={`Selected: ${windowLabel}`} title="Follows the period filter above." />}
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
          Marketing-sourced deals by pipeline. The chart stays visible; expand the table only when you need Pipeline → Sales POC → deal detail.
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, height: 256 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 8, right: 12, left: -18, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
            <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} tick={{ fontSize: 10, fill: '#64748b' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
            <RTooltip content={<ChartTip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="opportunities" name="Opportunities" fill="#4f46e5" radius={[5, 5, 0, 0]} />
            <Bar dataKey="customersClosed" name="Customers · Closed" fill="#06b6d4" radius={[5, 5, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ px: 2.5, pt: 1, pb: detailsOpen ? 0 : 2.5 }}>
        <ToggleBar open={detailsOpen} onClick={() => setDetailsOpen(!detailsOpen)}>
          <strong>{detailsOpen ? 'Hide pipeline, POC & deal table' : 'Show pipeline, POC & deal table'}</strong>
        </ToggleBar>
      </Box>
      {detailsOpen && (
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <MfDataTable
            rows={pipelines}
            rowKey={(p) => p.pipelineId}
            emptyText="Pipelines appear after HubSpot sync."
            onRowClick={(p) => { setOpenPipe(openPipe === p.pipelineId ? null : p.pipelineId); setOpenPoc(null) }}
            isRowActive={(p) => openPipe === p.pipelineId}
            headerGroups={OPP_CLOSED_GROUPS}
            columns={[
              { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (p) => p.pipelineLabel, cell: (p) => <DrillName open={openPipe === p.pipelineId} name={stripPipeline(p.pipelineLabel)} bold /> },
              { key: 'opportunities', header: 'Opportunities', title: 'Deals created in the window.', sortValue: (p) => p.opportunities, cell: (p) => integer(p.opportunities) },
              { key: 'wipNew', header: 'WIP · New', title: 'Work In Progress — created in window, still open.', sortValue: (p) => p.wipNew, cell: (p) => integer(p.wipNew) },
              { key: 'wipTotal', header: 'WIP · Total', title: 'Every currently-open deal in this pipeline, any creation date.', sortValue: (p) => p.wipTotal, cell: (p) => integer(p.wipTotal) },
              { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (p) => p.customersClosed, cell: (p) => integer(p.customersClosed) },
              { key: 'customersNew', header: 'Customers · New', title: 'Deals created AND won-closed within the window.', sortValue: (p) => p.customersNew, cell: (p) => integer(p.customersNew) },
              { key: 'shipPot', header: 'Ship. Potential', group: 'a', edge: 'left', sortValue: (p) => p.shipmentPotential, cell: (p) => integer(p.shipmentPotential) },
              { key: 'estMbr', header: 'Est. MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', group: 'a', edge: 'right', sortValue: (p) => p.estimatedMbr, cell: (p) => compactCurrency(p.estimatedMbr) },
              { key: 'closedShipPot', header: 'Ship. Potential', group: 'b', edge: 'left', sortValue: (p) => p.closedShipmentPotential, cell: (p) => integer(p.closedShipmentPotential) },
              { key: 'closedEstMbr', header: 'Est. MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', group: 'b', edge: 'right', sortValue: (p) => p.closedEstimatedMbr, cell: (p) => compactCurrency(p.closedEstimatedMbr) },
            ]}
            renderExpanded={(p) => (openPipe === p.pipelineId ? (
              <Box sx={drillBox()}>
                <MfDataTable
                  rows={p.pocs}
                  rowKey={(poc) => `${p.pipelineId}:${poc.owner}`}
                  onRowClick={(poc) => { const key = `${p.pipelineId}:${poc.owner}`; setOpenPoc(openPoc === key ? null : key) }}
                  isRowActive={(poc) => openPoc === `${p.pipelineId}:${poc.owner}`}
                  headerGroups={OPP_CLOSED_GROUPS}
                  columns={[
                    { key: 'owner', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (poc) => poc.owner, cell: (poc) => <DrillName open={openPoc === `${p.pipelineId}:${poc.owner}`} name={poc.owner} team={poc.team} /> },
                    { key: 'opps', header: 'Opps', title: 'Marketing deals (opportunities) created in the period.', sortValue: (poc) => poc.opportunities, cell: (poc) => integer(poc.opportunities) },
                    { key: 'wipNew', header: 'WIP · New', title: 'Work In Progress — created in window, still open.', sortValue: (poc) => poc.wipNew, cell: (poc) => integer(poc.wipNew) },
                    { key: 'wipTotal', header: 'WIP · Total', title: 'Every currently-open deal for this POC in this pipeline, any creation date.', sortValue: (poc) => poc.wipTotal, cell: (poc) => integer(poc.wipTotal) },
                    { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (poc) => poc.customersClosed, cell: (poc) => integer(poc.customersClosed) },
                    { key: 'customersNew', header: 'Customers · New', title: 'Deals created AND won-closed within the window.', sortValue: (poc) => poc.customersNew, cell: (poc) => integer(poc.customersNew) },
                    { key: 'shipPot', header: 'Ship. Potential', group: 'a', edge: 'left', sortValue: (poc) => poc.shipmentPotential, cell: (poc) => integer(poc.shipmentPotential) },
                    { key: 'estMbr', header: 'Est. MBR', group: 'a', edge: 'right', sortValue: (poc) => poc.monthlyMbr, cell: (poc) => compactCurrency(poc.monthlyMbr) },
                    { key: 'closedShipPot', header: 'Ship. Potential', group: 'b', edge: 'left', sortValue: (poc) => poc.closedShipmentPotential, cell: (poc) => integer(poc.closedShipmentPotential) },
                    { key: 'closedEstMbr', header: 'Est. MBR', group: 'b', edge: 'right', sortValue: (poc) => poc.closedMonthlyMbr, cell: (poc) => compactCurrency(poc.closedMonthlyMbr) },
                  ]}
                  renderExpanded={(poc) => (openPoc === `${p.pipelineId}:${poc.owner}` ? (
                    <Box sx={drillBox('#fff')}>
                      <MfDataTable rows={poc.deals} rowKey={(d) => d.dealId} emptyText="No deals created or won in this window." columns={dealColumns()} />
                    </Box>
                  ) : null)}
                />
              </Box>
            ) : null)}
          />
        </Box>
      )}
    </Card>
  )
}

// ── D2C Freight funnel: four clickable macro-stages ──────────────────────────
const STAGES = [
  { id: 1, tag: 'Stage 1', kicker: 'Lead Generation · all pipelines', title: 'Marketing Sources' },
  { id: 2, tag: 'Stage 2', kicker: 'Lead to Opportunity · D2C only', title: 'Qualified Leads' },
  { id: 3, tag: 'Stage 3', kicker: 'Opportunity to Deal', title: 'Sales POCs' },
  { id: 4, tag: 'Stage 4', kicker: 'Deal to Wins / WIP / Lost', title: 'Deal Outcomes' },
]

function StageTile({ label, value, hint }) {
  return (
    <Box sx={{ borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.05)', px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.5)' }}>
        {label}
        {hint && <Tooltip title={hint} arrow><InfoOutlinedIcon sx={{ fontSize: 12, cursor: 'help' }} /></Tooltip>}
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 22, fontWeight: 600, color: '#fff' }}>{value}</Typography>
    </Box>
  )
}

function SourceLabel({ name }) {
  if (!isAbmSource(name)) return <strong>{name ?? '—'}</strong>
  return (
    <Tooltip title={ABM_LEAD_BASIS} arrow>
      <strong>
        {name}
        <Box component="sup" sx={{ ml: 0.5, px: 0.5, borderRadius: '4px', fontSize: 9, fontWeight: 600, color: '#4f46e5', bgcolor: '#eef2ff', cursor: 'help' }}>reply-dated</Box>
      </strong>
    </Tooltip>
  )
}

function AbmLeadTableInline({ contacts, hub }) {
  const rec = (id) => (hub?.portalId ? `https://${hub.uiDomain}/contacts/${hub.portalId}/record/0-1/${id}` : undefined)
  return (
    <Box sx={drillBox()}>
      <MfDataTable
        rows={contacts}
        rowKey={(c) => c.contactId}
        dense
        initialPageSize={25}
        filter={(c, q) => `${c.name} ${c.email ?? ''} ${c.company ?? ''} ${c.campaign ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter contacts…"
        emptyText="No ABM leads in this window."
        columns={[
          {
            key: 'name', header: 'Contact', cellSx: { ...wrapSx, minWidth: 200 }, sortValue: (c) => c.name,
            cell: (c) => (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}>
                  <strong>{c.name}</strong>
                  {rec(c.contactId) && (
                    <Box component="a" href={rec(c.contactId)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 11.5, color: '#4338ca', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      HubSpot <OpenInNewIcon sx={{ fontSize: 11 }} />
                    </Box>
                  )}
                </Box>
                {c.email && <Typography sx={{ fontSize: 11.5, color: INK_400 }}>{c.email}</Typography>}
              </Box>
            ),
          },
          { key: 'company', header: 'Company', sortValue: (c) => c.company ?? '', cell: (c) => c.company ?? '—', cellSx: { ...wrapSx, minWidth: 140 } },
          {
            key: 'replied', header: 'Replied', title: 'HubSpot "ABM Last Replied Date" — the date this lead is counted on (contacts that never replied count on their create date).',
            sortValue: (c) => c.repliedDate,
            cell: (c) => (c.replied ? c.repliedDate : <span title="Never replied — counted on contact create date">{c.repliedDate} <span style={{ color: INK_400 }}>(created)</span></span>),
          },
          { key: 'campaign', header: 'Campaign', sortValue: (c) => c.campaign ?? '', cell: (c) => c.campaign ?? '—', cellSx: { ...wrapSx, minWidth: 180 } },
          { key: 'stage', header: 'Lifecycle', sortValue: (c) => c.lifecycleStage ?? '', cell: (c) => c.lifecycleStage ?? '—' },
          {
            key: 'sub', header: 'Sub-source', title: 'Not used for counting. Flagged when it is not ABM, so the record can be fixed in HubSpot.',
            sortValue: (c) => c.subSource,
            cell: (c) => (c.subSourceNeedsFix
              ? <Box component="span" title="Not set to ABM in HubSpot — worth fixing at source." sx={{ px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 11, fontWeight: 600, color: '#9a3412', bgcolor: '#ffedd5' }}>{c.subSource}</Box>
              : c.subSource),
          },
        ]}
      />
    </Box>
  )
}

export function D2CFunnel({ data }) {
  const [openStage, setOpenStage] = useState(null)
  const [openOutcomeOwner, setOpenOutcomeOwner] = useState(null)
  const [openLeadSource, setOpenLeadSource] = useState(null)
  const d = data.d2c
  if (!d) return null
  const stage = openStage ?? 1
  const stageSub = (id) => (id === 1 ? `Leads (all pipelines): ${integer(d.stage1.leads)}`
    : id === 2 ? `Total Opportunities: ${integer(d.stage2.opportunities)}`
      : id === 3 ? `Sales POCs: ${integer(d.stage3.salesPocs)} · ${integer(d.stage3.deals)} deals`
        : `Customers · Closed: ${integer(d.stage4.customersClosed)}`)
  const band = integer(d.stage2.shipmentThreshold)
  const windowLabel = data.windowDetail?.label || data.window?.label

  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden', borderRadius: '28px', border: '1px solid rgba(79,70,229,0.14)', p: 3, mb: 3,
      background: 'linear-gradient(160deg, #eef2ff 0%, #ffffff 55%, #f5f3ff 100%)',
      boxShadow: '0 24px 48px -22px rgba(79,70,229,0.38)',
      '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 4, background: 'linear-gradient(90deg,#4f46e5,#06b6d4)' },
    }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <SectionTitle icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}>D2C Freight funnel</SectionTitle>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {windowLabel && <ScopeTag label={`Selected: ${windowLabel}`} title="Follows the period filter above." />}
          {data.d2cScope?.pipelineLabel && (
            <ScopeTag label={`${data.d2cScope.pipelineLabel} · marketing-sourced`} title={`Pipeline id ${data.d2cScope.pipelineId}, resolved once by the scope layer`} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {STAGES.map((s) => {
          const active = s.id === openStage
          return (
            <Box key={s.id} component="button" aria-expanded={active} onClick={() => setOpenStage(openStage === s.id ? null : s.id)} sx={{
              textAlign: 'left', cursor: 'pointer', borderRadius: '16px', p: 2, transition: 'all 200ms',
              border: active ? '1px solid transparent' : '1px solid rgba(15,23,42,0.06)',
              background: active ? 'linear-gradient(155deg, #1e1b4b 0%, #0b1220 65%)' : 'linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: active ? '0 12px 28px -8px rgba(79,70,229,0.55)' : '0 10px 20px -10px rgba(79,70,229,0.22)',
              '&:hover': { transform: active ? 'none' : 'translateY(-2px)' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: active ? 'rgba(255,255,255,.55)' : INK_400 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#4f46e5,#06b6d4)' }}>{s.id}</Box>
                {s.tag}
              </Box>
              <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: active ? 'rgba(255,255,255,.6)' : INK_400 }}>{s.kicker}</Typography>
              <Typography sx={{ mt: 0.25, fontSize: 18, fontWeight: 600, color: active ? '#fff' : '#1e293b' }}>{s.title}</Typography>
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12, color: active ? 'rgba(255,255,255,.7)' : GX_INK_500 }}>
                <span>{stageSub(s.id)}</span><Chevron open={active} size={15} />
              </Box>
            </Box>
          )
        })}
      </Box>

      {openStage !== null && (
        <Box sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '24px',
          background: 'linear-gradient(155deg, #161b33 0%, #0b1220 65%)',
          boxShadow: '0 24px 48px -16px rgba(79,70,229,0.45)',
          '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 3, background: 'linear-gradient(90deg,#4f46e5,#06b6d4)' },
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 2.5 }}>
            <Box sx={{ mr: 'auto' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.45)' }}>{STAGES[stage - 1].kicker}</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>{STAGES[stage - 1].title}</Typography>
            </Box>
            {stage === 1 && <StageTile label="Total Leads" value={integer(d.stage1.leads)} />}
            {stage === 2 && (
              <>
                <StageTile label="Total Opps" value={integer(d.stage2.opportunities)} />
                <StageTile label="Lead → Opp %" value={percentage(d.stage2.leadToOpportunityPct)} hint="D2C marketing opportunities ÷ marketing leads (leads are not pipeline-bound)." />
                <StageTile label={`< ${band} / ≥ ${band} shpmts`} value={`${integer(d.stage2.small)} / ${integer(d.stage2.large)}`} hint="Deals split by expected monthly shipments (SME band)." />
              </>
            )}
            {stage === 3 && (
              <>
                <StageTile label="Sales POCs" value={integer(d.stage3.salesPocs)} />
                <StageTile label="SME BD" value={integer(d.stage3.sme)} />
                <StageTile label="Enterprise BD" value={integer(d.stage3.enterprise)} />
                <StageTile label="Deals" value={integer(d.stage3.deals)} />
                <StageTile label="Shipment Potential" value={integer(d.stage3.shipmentPotential)} hint="Σ expected monthly shipments over the created-deal cohort." />
              </>
            )}
            {stage === 4 && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', bgcolor: 'rgba(255,255,255,0.05)', p: 1 }}>
                  <StageTile label="Deals" value={integer(d.stage4.deals)} />
                  <StageTile label="WIP · New" value={integer(d.stage4.wipNew)} hint="Created in this window and currently still open." />
                  <StageTile label="Lost" value={integer(d.stage4.lost)} />
                  <StageTile label="Customers · New" value={integer(d.stage4.customersNew)} hint="Created and closed within this window — this window's created cohort, boxed together." />
                </Box>
                <StageTile label="WIP · Total" value={integer(d.stage4.wipTotal)} hint="Every currently-open D2C deal, any creation date — a live backlog gauge, independent of the selected window." />
                <StageTile label="Customers · Closed" value={integer(d.stage4.customersClosed)} hint="Closed in this window regardless of when the deal was created — not part of the Deals total to the left." />
                <StageTile label="Assigned Shipments" value={integer(d.stage4.assignedShipments)} hint="Σ expected monthly shipments of the Customers · Closed cohort (deals won in the window)." />
                <StageTile label="Achieved Shipments" value={integer(d.stage4.achievedShipments)} hint="ProShip actual shipments (daily grain) of the Customers · Closed cohort within the window." />
              </>
            )}
          </Box>
          <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '0 0 24px 24px', overflowX: 'auto' }}>
            {stage === 1 && (
              <MfDataTable
                rows={d.stage1.sources}
                rowKey={(r) => r.subcategory}
                emptyText="No marketing leads in this window."
                onRowClick={(r) => (isAbmSource(r.subcategory) ? setOpenLeadSource(openLeadSource === r.subcategory ? null : r.subcategory) : undefined)}
                isRowActive={(r) => openLeadSource === r.subcategory}
                renderExpanded={(r) => (isAbmSource(r.subcategory) && openLeadSource === r.subcategory
                  ? <AbmLeadTableInline contacts={data.abmLeads?.rows ?? []} hub={data.hubspot} />
                  : null)}
                columns={[
                  {
                    key: 'source', header: 'Marketing Source', sortValue: (r) => r.subcategory,
                    cell: (r) => (isAbmSource(r.subcategory)
                      ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#4338ca' }}><Chevron open={openLeadSource === r.subcategory} size={14} /><SourceLabel name={r.subcategory} /></Box>
                      : <SourceLabel name={r.subcategory} />),
                  },
                  { key: 'leads', header: 'No. of Leads', title: 'Leads created in the window. EXCEPTION: the ABM row is counted on reply date — hover the ABM label.', sortValue: (r) => r.leads, cell: (r) => integer(r.leads) },
                  { key: 'budget', header: 'Budget', sortValue: (r) => r.budget, cell: (r) => (r.budget === null || r.budget === undefined ? '—' : currency(r.budget)) },
                  { key: 'cpl', header: 'CPL', sortValue: (r) => r.costPerLead, cell: (r) => currency(r.costPerLead) },
                ]}
              />
            )}
            {stage === 2 && (
              <MfDataTable
                rows={d.stage2.qualifiedBy}
                rowKey={(r) => r.owner}
                emptyText="No opportunities created in this window."
                columns={[
                  { key: 'owner', header: 'Qualified By', sortValue: (r) => r.owner, cell: (r) => <strong>{r.owner}</strong> },
                  { key: 'team', header: 'Team', title: "The Sales POC's team tag.", sortValue: (r) => r.team ?? '', cell: (r) => r.team ?? '—' },
                  { key: 'opps', header: 'No. of Opps', sortValue: (r) => r.opportunities, cell: (r) => integer(r.opportunities) },
                  { key: 'small', header: '< band', sortValue: (r) => r.small, cell: (r) => integer(r.small) },
                  { key: 'large', header: '≥ band', sortValue: (r) => r.large, cell: (r) => integer(r.large) },
                ]}
              />
            )}
            {stage === 3 && (
              <MfDataTable
                rows={d.stage3.rows}
                rowKey={(r) => r.owner}
                emptyText="No deals created in this window."
                columns={[
                  { key: 'owner', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (r) => r.owner, cell: (r) => <strong>{r.owner}</strong> },
                  { key: 'team', header: 'BD Leader Type', sortValue: (r) => r.team ?? '', cell: (r) => r.team ?? '—' },
                  { key: 'deals', header: 'No. of Deals', sortValue: (r) => r.deals, cell: (r) => integer(r.deals) },
                  { key: 'shipPot', header: 'Shipment Potential', title: 'Expected monthly shipments (orders/month) on the deal.', sortValue: (r) => r.shipmentPotential, cell: (r) => integer(r.shipmentPotential) },
                  { key: 'pctShip', header: '% of Shipment Potential', sortValue: (r) => r.pctShipmentPotential, cell: (r) => percentage(r.pctShipmentPotential) },
                ]}
              />
            )}
            {stage === 4 && (
              <MfDataTable
                rows={d.stage4.rows}
                rowKey={(r) => r.personId || `name:${r.owner}`}
                emptyText="No D2C deals or customers in this window."
                onRowClick={(r) => setOpenOutcomeOwner(openOutcomeOwner === r.owner ? null : r.owner)}
                isRowActive={(r) => openOutcomeOwner === r.owner}
                headerGroups={[
                  { label: '', span: 2 },
                  { label: 'Created Cohort · Deals = WIP · New + Lost + Customers · New', span: 4, variant: 'a' },
                  { label: '', span: 7 },
                ]}
                columns={[
                  { key: 'owner', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (r) => r.owner, cell: (r) => <DrillName open={openOutcomeOwner === r.owner} name={r.owner} /> },
                  { key: 'team', header: 'BD Leader Type', sortValue: (r) => r.team ?? '', cell: (r) => r.team ?? '—' },
                  { key: 'deals', header: 'No. of Deals', group: 'a', edge: 'left', sortValue: (r) => r.deals, cell: (r) => integer(r.deals) },
                  { key: 'wipNew', header: 'WIP · New', title: 'Work In Progress — created in window, still open.', group: 'a', sortValue: (r) => r.wipNew, cell: (r) => integer(r.wipNew) },
                  { key: 'lost', header: 'Lost Deals', group: 'a', sortValue: (r) => r.lost, cell: (r) => integer(r.lost) },
                  { key: 'customersNew', header: 'Customers · New', title: 'Deals created AND won-closed within the window.', group: 'a', edge: 'right', sortValue: (r) => r.customersNew, cell: (r) => integer(r.customersNew) },
                  { key: 'wipTotal', header: 'WIP · Total', title: 'Every currently-open D2C deal for this POC, any creation date — independent of the selected window.', sortValue: (r) => r.wipTotal, cell: (r) => integer(r.wipTotal) },
                  { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (r) => r.customersClosed, cell: (r) => integer(r.customersClosed) },
                  { key: 'closurePctNew', header: 'Closure % (New)', sortValue: (r) => r.closurePctNew, cell: (r) => percentage(r.closurePctNew) },
                  { key: 'closurePctClosed', header: 'Closure % (Closed)', sortValue: (r) => r.closurePctClosed, cell: (r) => percentage(r.closurePctClosed) },
                  { key: 'assignedShipments', header: 'Assigned Shipments', sortValue: (r) => r.assignedShipments, cell: (r) => integer(r.assignedShipments) },
                  { key: 'achievedShipments', header: 'Achieved Shipments', sortValue: (r) => r.achievedShipments, cell: (r) => <span title={r.achievedShipments === null ? 'No ProShip actuals loaded.' : undefined}>{integer(r.achievedShipments)}</span> },
                  { key: 'assignedVsAchieved', header: 'Assigned vs Achieved Shipment %', sortValue: (r) => r.assignedVsAchievedShipmentPct, cell: (r) => percentage(r.assignedVsAchievedShipmentPct) },
                ]}
                renderExpanded={(r) => (openOutcomeOwner === r.owner ? (
                  <Box sx={drillBox()}>
                    <MfDataTable
                      rows={r.bySource}
                      rowKey={(s) => s.subcategory}
                      emptyText="No deals for this POC in the window."
                      columns={[
                        { key: 'source', header: 'Deal Source', sortValue: (s) => s.subcategory, cell: (s) => <strong>{s.subcategory}</strong> },
                        { key: 'deals', header: 'No. of Deals', sortValue: (s) => s.deals, cell: (s) => integer(s.deals) },
                        { key: 'wipNew', header: 'WIP · New', title: 'Created in window, still open.', sortValue: (s) => s.wipNew, cell: (s) => integer(s.wipNew) },
                        { key: 'wipTotal', header: 'WIP · Total', title: 'All open deals, any create date (live snapshot).', sortValue: (s) => s.wipTotal, cell: (s) => integer(s.wipTotal) },
                        { key: 'lost', header: 'Lost Deals', sortValue: (s) => s.lost, cell: (s) => integer(s.lost) },
                        { key: 'customersNew', header: 'Customers · New', title: 'Deals created AND won-closed within the window.', sortValue: (s) => s.customersNew, cell: (s) => integer(s.customersNew) },
                        { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (s) => s.customersClosed, cell: (s) => integer(s.customersClosed) },
                      ]}
                    />
                  </Box>
                ) : null)}
              />
            )}
          </Box>
        </Box>
      )}
      <Typography sx={{ mt: 2, fontSize: 12, color: GX_INK_500 }}>
        Stage 4’s boxed columns (No. of Deals, WIP · New, Lost, Customers · New) are this window&apos;s <strong>created</strong> cohort.
        WIP · New and WIP · Total are two different questions: New = created in this window and still open; Total = every currently-open
        deal for this POC, any creation date — a live backlog gauge. Customers · Closed sits outside the box because it includes deals{' '}
        <strong>closed</strong> in the window regardless of when they were created. <strong>Assigned &amp; Achieved Shipments are measured
        against the Customers · Closed cohort</strong> — the expected monthly potential of the customers we closed this window vs what they
        actually shipped in-window (ProShip daily actuals). <strong>Closure %</strong> is the win rate = won ÷ (won + lost). Click a Sales POC
        row to see their deals by Deal Source.
      </Typography>
    </Box>
  )
}

// ── Platform performance (per marketing channel) ─────────────────────────────
export function PlatformPerformance({ platforms }) {
  const [idx, setIdx] = useState(0)
  const [open, setOpen] = useState(false)
  const [consolidatedOpen, setConsolidatedOpen] = useState(false)
  const [openSource, setOpenSource] = useState(null)
  const [openOwner, setOpenOwner] = useState(null)
  if (!platforms || !platforms.length) {
    return (
      <Card sx={{ p: 2.5 }}>
        <SectionTitle>Platform Performance</SectionTitle>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: INK_400 }}>No marketing-channel activity yet — sources appear after a HubSpot sync captures channel leads or deals.</Typography>
      </Card>
    )
  }
  const p = platforms[Math.min(idx, platforms.length - 1)]
  const chart = p.monthly.map((m) => ({ ...m, name: monthLabel(m.month), spendVal: m.spend ?? 0 }))
  const trailingLabel = p.monthly.length === 12 ? `${monthLabel(p.monthly[0].month)} – ${monthLabel(p.monthly[11].month)}` : undefined
  const windowLabel = p.windowed.label
  const w = p.windowed

  return (
    <Card sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ flex: '1 1 420px' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <SectionTitle icon={<BarChartIcon sx={{ fontSize: 18 }} />}>Platform performance</SectionTitle>
            {trailingLabel && <ScopeTag label={`Chart: Trailing 12mo ${trailingLabel}`} title="The chart is always the last 12 calendar months, independent of the period filter above." />}
            <ScopeTag label={`Tables: Selected ${windowLabel}`} title="The KPI rail and both tables below follow the period filter above." />
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
            The chart is trailing 12 months; the KPI rail and both tables below follow the period filter above.{' '}
            <strong>Customers · Converted</strong> is a cohort count: leads/opportunities from the period who have <em>ever</em> reached Won,
            however long that took — not a close-date snapshot (that&apos;s Customers · Closed elsewhere on this dashboard).
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, border: `1px solid ${GX_BORDER}`, borderRadius: '12px', p: 0.5 }}>
          {platforms.map((pl, i) => (
            <Box key={pl.subcategoryId} component="button" onClick={() => setIdx(i)} sx={{
              border: 'none', cursor: 'pointer', borderRadius: '8px', px: 1.5, py: 0.75, fontSize: 12, fontWeight: 500,
              bgcolor: i === idx ? '#4f46e5' : 'transparent', color: i === idx ? '#fff' : GX_INK_500,
              '&:hover': { bgcolor: i === idx ? '#4f46e5' : '#f1f5f9' },
            }}>{pl.subcategory}</Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' }, gap: 2 }}>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} margin={{ top: 8, right: 8, left: -18, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
              <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 10, fill: '#64748b' }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => compactCurrency(v)} />
              <RTooltip content={<ChartTip moneyKeys={['spendVal']} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="right" dataKey="spendVal" name="Total Spent" fill="#c7d2fe" radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="leads" name="Total Leads" stroke="#4f46e5" strokeWidth={2.5} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="opportunities" name="Opportunities" stroke="#0d9488" strokeWidth={2.5} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="customersConverted" name="Customers · Converted" stroke="#16a34a" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: '1fr' }, gap: 1.5 }}>
          <Tile3d label={`Selected period · ${p.subcategory}`} value={windowLabel} />
          <Tile3d label="Cost per Lead" value={currency(w.costPerLead)} sub={`${integer(w.leads)} leads`} />
          <Tile3d label="Cost per Opportunity" value={currency(w.costPerOpportunity)} sub={`${integer(w.opportunities)} opps`} />
          <Tile3d label="Cost per Converted Customer" labelTitle="Spend ÷ Customers · Converted (created-in-period cohort that ever reached Won). A DIFFERENT denominator from the headline Cost per Customer."
            value={currency(w.costPerCustomer)} sub={`${integer(w.customersConverted)} customers converted`} subTitle="Leads/opportunities created in the selected period that have ever converted, not a close-date count." />
          <Tile3d label="Lead → Opp" value={percentage(w.leadToOpportunityPct)} />
          <Tile3d label="Opp → Customer" value={percentage(w.opportunityToCustomerPct)} />
        </Box>
      </Box>

      <ToggleBar open={open} onClick={() => setOpen(!open)} sx={{ mt: 2 }}>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569' }}>
          {p.subcategory} — MoM performance analysis ({windowLabel})
        </Typography>
      </ToggleBar>
      {open && (
        <Box sx={{ mt: 1.5 }}>
          <MfDataTable
            rows={p.windowedMonthly}
            rowKey={(m) => m.month}
            columns={[
              { key: 'month', header: 'Month', title: 'Calendar month.', sortValue: (m) => m.month, cell: (m) => <strong>{monthLabel(m.month)}</strong> },
              { key: 'leads', header: 'Leads', title: 'Marketing leads created in the period.', sortValue: (m) => m.leads, cell: (m) => integer(m.leads) },
              { key: 'spent', header: 'Spent', title: 'Marketing spend — month × channel budget, prorated on partial months.', sortValue: (m) => m.spend, cell: (m) => (m.spend === null || m.spend === undefined ? '—' : currency(m.spend)) },
              { key: 'cpl', header: 'CPL', title: 'Cost per Lead — spend ÷ leads', sortValue: (m) => m.costPerLead, cell: (m) => currency(m.costPerLead) },
              { key: 'opps', header: 'Opps', title: 'Marketing deals (opportunities) created in the period.', sortValue: (m) => m.opportunities, cell: (m) => integer(m.opportunities) },
              { key: 'cpo', header: 'CPO', title: 'Cost per Opportunity — spend ÷ opportunities', sortValue: (m) => m.costPerOpportunity, cell: (m) => currency(m.costPerOpportunity) },
              { key: 'leadToOpp', header: 'Lead→Opp', sortValue: (m) => m.leadToOpportunityPct, cell: (m) => percentage(m.leadToOpportunityPct) },
              { key: 'converted', header: 'Customers · Converted', title: 'Leads/opportunities created this month who have ever reached Won.', sortValue: (m) => m.customersConverted, cell: (m) => integer(m.customersConverted) },
              { key: 'cpcc', header: 'CPCC', title: 'Cost per Converted Customer — spend ÷ Customers · Converted (NOT cost-per-click)', sortValue: (m) => m.costPerCustomer, cell: (m) => currency(m.costPerCustomer) },
              { key: 'oppToCust', header: 'Opp→Cust', sortValue: (m) => m.opportunityToCustomerPct, cell: (m) => percentage(m.opportunityToCustomerPct) },
              { key: 'leadToCust', header: 'Lead→Cust', sortValue: (m) => m.leadToCustomerPct, cell: (m) => percentage(m.leadToCustomerPct) },
            ]}
          />
        </Box>
      )}

      <ToggleBar open={consolidatedOpen} onClick={() => setConsolidatedOpen(!consolidatedOpen)} sx={{ mt: 1.5 }}>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569' }}>
          All sources — consolidated ({windowLabel})
        </Typography>
      </ToggleBar>
      {consolidatedOpen && (
        <Box sx={{ mt: 1.5 }}>
          <MfDataTable
            rows={platforms}
            rowKey={(pl) => pl.subcategoryId}
            emptyText="Sources appear after HubSpot sync."
            onRowClick={(pl) => { setOpenSource(openSource === pl.subcategoryId ? null : pl.subcategoryId); setOpenOwner(null) }}
            isRowActive={(pl) => openSource === pl.subcategoryId}
            columns={[
              { key: 'source', header: 'Source', title: 'Deal source.', sortValue: (pl) => pl.subcategory, cell: (pl) => <DrillName open={openSource === pl.subcategoryId} name={pl.subcategory} /> },
              { key: 'period', header: 'Period', title: 'The selected reporting window.', sortValue: (pl) => pl.windowed.label, cell: (pl) => pl.windowed.label },
              { key: 'leads', header: 'Leads', title: 'Marketing leads created in the period.', sortValue: (pl) => pl.windowed.leads, cell: (pl) => integer(pl.windowed.leads) },
              { key: 'spent', header: 'Spent', title: 'Marketing spend — month × channel budget, prorated on partial months.', sortValue: (pl) => pl.windowed.spend, cell: (pl) => (pl.windowed.spend === null || pl.windowed.spend === undefined ? '—' : currency(pl.windowed.spend)) },
              { key: 'cpl', header: 'CPL', title: 'Cost per Lead — spend ÷ leads', sortValue: (pl) => pl.windowed.costPerLead, cell: (pl) => currency(pl.windowed.costPerLead) },
              { key: 'opps', header: 'Opps', title: 'Marketing deals (opportunities) created in the period.', sortValue: (pl) => pl.windowed.opportunities, cell: (pl) => integer(pl.windowed.opportunities) },
              { key: 'cpo', header: 'CPO', title: 'Cost per Opportunity — spend ÷ opportunities', sortValue: (pl) => pl.windowed.costPerOpportunity, cell: (pl) => currency(pl.windowed.costPerOpportunity) },
              { key: 'leadToOpp', header: 'Lead→Opp', sortValue: (pl) => pl.windowed.leadToOpportunityPct, cell: (pl) => percentage(pl.windowed.leadToOpportunityPct) },
              { key: 'converted', header: 'Customers · Converted', title: 'Leads/opportunities created in the period that have ever reached Won.', sortValue: (pl) => pl.windowed.customersConverted, cell: (pl) => integer(pl.windowed.customersConverted) },
              { key: 'cpcc', header: 'CPCC', title: 'Cost per Converted Customer — spend ÷ Customers · Converted (NOT cost-per-click)', sortValue: (pl) => pl.windowed.costPerCustomer, cell: (pl) => currency(pl.windowed.costPerCustomer) },
              { key: 'mbr', header: 'Est. MBR', title: "Σ Est. MBR (monthly ₹) of this channel's created-in-period deals.", sortValue: (pl) => pl.windowed.estimatedMbr, cell: (pl) => compactCurrency(pl.windowed.estimatedMbr) },
              { key: 'mbrPerSpend', header: 'MBR÷Spend', title: '₹ monthly pipeline generated per ₹ spent. Shows — until budgets are entered.', sortValue: (pl) => pl.windowed.mbrPerSpend, cell: (pl) => (pl.windowed.mbrPerSpend == null ? '—' : `${pl.windowed.mbrPerSpend}×`) },
              { key: 'oppToCust', header: 'Opp→Cust', sortValue: (pl) => pl.windowed.opportunityToCustomerPct, cell: (pl) => percentage(pl.windowed.opportunityToCustomerPct) },
              { key: 'leadToCust', header: 'Lead→Cust', sortValue: (pl) => pl.windowed.leadToCustomerPct, cell: (pl) => percentage(pl.windowed.leadToCustomerPct) },
            ]}
            renderExpanded={(pl) => (openSource === pl.subcategoryId ? (
              <Box sx={drillBox()}>
                {pl.windowed.byOwner.length === 0 ? (
                  <Typography sx={{ p: 1.5, fontSize: 13, color: GX_INK_500 }}>No converted customers from this source in {pl.windowed.label}.</Typography>
                ) : (
                  <MfDataTable
                    rows={pl.windowed.byOwner}
                    rowKey={(poc) => `${pl.subcategoryId}:${poc.owner}`}
                    onRowClick={(poc) => { const key = `${pl.subcategoryId}:${poc.owner}`; setOpenOwner(openOwner === key ? null : key) }}
                    isRowActive={(poc) => openOwner === `${pl.subcategoryId}:${poc.owner}`}
                    columns={[
                      { key: 'poc', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (poc) => poc.owner, cell: (poc) => <DrillName open={openOwner === `${pl.subcategoryId}:${poc.owner}`} name={poc.owner} team={poc.team} /> },
                      { key: 'converted', header: 'Customers · Converted', sortValue: (poc) => poc.customersConverted, cell: (poc) => integer(poc.customersConverted) },
                    ]}
                    renderExpanded={(poc) => (openOwner === `${pl.subcategoryId}:${poc.owner}` ? (
                      <Box sx={drillBox('#fff')}>
                        <MfDataTable rows={poc.deals} rowKey={(d) => d.dealId} columns={dealColumns()} />
                      </Box>
                    ) : null)}
                  />
                )}
              </Box>
            ) : null)}
          />
        </Box>
      )}
    </Card>
  )
}

// ── Sales POC-wise scoreboard (Customers · Closed by owner + brand drill) ────
export function PocScoreboard({ board, windowLabel }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [openOwner, setOpenOwner] = useState(null)
  const rows = board?.rows ?? []
  return (
    <Card>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<GroupsIcon sx={{ fontSize: 18 }} />}>Sales POC-wise scoreboard</SectionTitle>
          {windowLabel && <ScopeTag label={`Selected: ${windowLabel}`} title="Follows the period filter above." />}
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
          Marketing-sourced customers closed in the window by Sales POC. Expand the table, then click a name for the brand-level drill-down.
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <ToggleBar open={detailsOpen} onClick={() => setDetailsOpen(!detailsOpen)}>
          <strong>{detailsOpen ? 'Hide Sales POC table' : 'Show Sales POC table'}</strong>
        </ToggleBar>
        {detailsOpen && (
          <Box sx={{ mt: 2 }}>
            <MfDataTable
              rows={rows}
              rowKey={(r) => r.owner}
              emptyText="No customers closed in this window."
              onRowClick={(r) => setOpenOwner(openOwner === r.owner ? null : r.owner)}
              isRowActive={(r) => openOwner === r.owner}
              footer={['TOTAL', integer(board.totals.customersClosed), integer(board.totals.monthlyShipmentPotential), currency(board.totals.monthlyMbr)]}
              columns={[
                { key: 'owner', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (r) => r.owner, cell: (r) => <DrillName open={openOwner === r.owner} name={r.owner} team={r.team} /> },
                { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (r) => r.customersClosed, cell: (r) => integer(r.customersClosed) },
                { key: 'shipPot', header: 'Monthly Shipment Potential', sortValue: (r) => r.monthlyShipmentPotential, cell: (r) => integer(r.monthlyShipmentPotential) },
                { key: 'mbr', header: 'Monthly MBR (INR)', sortValue: (r) => r.monthlyMbr, cell: (r) => currency(r.monthlyMbr) },
              ]}
              renderExpanded={(r) => (openOwner === r.owner ? (
                <Box sx={drillBox()}>
                  <MfDataTable
                    rows={r.deals}
                    rowKey={(d) => d.dealId}
                    emptyText="Signed brands are hidden from display."
                    columns={[
                      { key: 'brand', header: 'Brand Name', title: 'Brand on the deal.', sortValue: (d) => d.brandName, cell: (d) => <strong>{d.brandName}</strong> },
                      { key: 'source', header: 'Source', title: 'Deal source.', sortValue: (d) => d.subcategory, cell: (d) => d.subcategory },
                      { key: 'website', header: 'Website', title: 'Brand website captured on the deal.', sortValue: (d) => d.website ?? '', cell: (d) => d.website ?? '—' },
                      { key: 'signUp', header: 'Sign-up Date', sortValue: (d) => d.signUpDate ?? '', cell: (d) => d.signUpDate ?? '—' },
                      { key: 'category', header: 'Product Category', title: 'Product category captured on the deal.', sortValue: (d) => d.productCategory ?? '', cell: (d) => d.productCategory ?? '—' },
                      { key: 'dailyOrders', header: 'Daily Orders', title: 'Expected daily orders on the deal.', sortValue: (d) => d.dailyOrders, cell: (d) => integer(d.dailyOrders) },
                      { key: 'monthlyOrders', header: 'Est. Monthly Orders', title: 'Expected monthly orders (shipment potential).', sortValue: (d) => d.estimatedMonthlyOrders, cell: (d) => integer(d.estimatedMonthlyOrders) },
                      { key: 'monthlyMbr', header: 'Est. Monthly MBR (INR)', sortValue: (d) => d.estimatedMonthlyMbr, cell: (d) => currency(d.estimatedMonthlyMbr) },
                    ]}
                  />
                </Box>
              ) : null)}
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}

// ── Closed Won Deals — the go-live worklist ──────────────────────────────────
export function ClosedWonDeals({ deals, windowLabel }) {
  const [open, setOpen] = useState(false)
  const list = deals || []
  const live = list.filter((d) => d.status === 'Live').length
  const awaiting = list.length - live
  return (
    <Card>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}>Closed Won Deals</SectionTitle>
          {windowLabel && <ScopeTag label={`Closed: ${windowLabel}`} title="Deals closed-won in the selected window. Actuals are all-time (has the brand gone live yet since we won it)." />}
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
          Every marketing deal won in the window and where it stands on going live — <strong>Live</strong> brands first, then those{' '}
          <strong>Awaiting go-live</strong> (won but not shipping yet), oldest-closed first so the aging ones stand out.{' '}
          {list.length} deals · {live} live · {awaiting} awaiting go-live.
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <ToggleBar open={open} onClick={() => setOpen(!open)}><strong>{open ? 'Hide table' : 'Show table'}</strong></ToggleBar>
        {open && (
          <Box sx={{ mt: 2 }}>
            <MfDataTable
              rows={list}
              rowKey={(d) => d.dealId}
              emptyText="No deals closed-won in this window."
              headerGroups={[
                { label: '', span: 7 },
                { label: 'Potential (Monthly run-rate)', span: 2, variant: 'a', title: 'Expected MONTHLY run-rate from the deal (shipments/month, MBR/month).' },
                { label: 'Actual (Lifetime, cumulative)', span: 2, variant: 'b', title: 'Lifetime ProShip totals since the brand went live — a cumulative figure.' },
              ]}
              columns={[
                { key: 'status', header: 'Status', sortValue: (d) => d.status, cell: (d) => <StatusPill status={d.status} /> },
                { key: 'dealName', header: 'Deal Name', sortValue: (d) => d.dealName, cell: (d) => <strong>{d.dealName}</strong> },
                { key: 'proshipName', header: 'ProShip Name', sortValue: (d) => d.proshipName ?? '', cell: (d) => d.proshipName ?? '—' },
                { key: 'salesPoc', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (d) => d.poc, cell: (d) => d.poc },
                { key: 'closedDate', header: 'Closed Date', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => shortDate(d.closedDate) },
                { key: 'liveDate', header: 'Brand Live Date', title: 'Date the brand went live — its first ProShip shipment.', sortValue: (d) => d.brandLiveDate ?? '', cell: (d) => shortDate(d.brandLiveDate) },
                { key: 'daysSince', header: 'Days Since Closed', title: "Days from the deal's close date to the end of the selected window.", sortValue: (d) => d.daysSinceClosed, cell: (d) => (d.daysSinceClosed == null ? '—' : integer(d.daysSinceClosed)) },
                { key: 'potShip', header: 'Shipments', title: 'Expected monthly shipments on the deal.', group: 'a', edge: 'left', sortValue: (d) => d.potentialShipments, cell: (d) => integer(d.potentialShipments) },
                { key: 'potRev', header: 'Revenue (MBR)', group: 'a', edge: 'right', sortValue: (d) => d.potentialRevenue, cell: (d) => currency(d.potentialRevenue) },
                { key: 'actShip', header: 'Shipments', title: 'Lifetime ProShip shipments.', group: 'b', edge: 'left', sortValue: (d) => d.actualShipments, cell: (d) => integer(d.actualShipments) },
                { key: 'actRev', header: 'Revenue', title: 'Lifetime ProShip revenue (₹).', group: 'b', edge: 'right', sortValue: (d) => d.actualRevenue, cell: (d) => currency(d.actualRevenue) },
              ]}
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}

// ── Sales POC & pipeline-wise closure % (pivot of the Pipeline scoreboard) ───
export function PocPipelineScoreboard({ rows, windowLabel }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [openOwner, setOpenOwner] = useState(null)
  const cols = (pipelineLevel) => [
    { key: 'dealsAssigned', header: 'Deals Assigned', sortValue: (r) => r.dealsAssigned, cell: (r) => integer(r.dealsAssigned) },
    { key: 'wipNew', header: 'WIP · New', title: 'Work In Progress — created in window, still open.', sortValue: (r) => r.wipNew, cell: (r) => integer(r.wipNew) },
    { key: 'wipTotal', header: 'WIP · Total', title: pipelineLevel ? 'All open deals in this pipeline, any create date (live snapshot).' : 'Every currently-open deal for this POC across all pipelines, any creation date.', sortValue: (r) => r.wipTotal, cell: (r) => integer(r.wipTotal) },
    { key: 'lost', header: 'Lost', title: 'Deals created in the window that are now Lost.', sortValue: (r) => r.lost, cell: (r) => integer(r.lost) },
    { key: 'customersNew', header: 'Customers · New', title: 'Deals created AND won-closed within the window.', sortValue: (r) => r.customersNew, cell: (r) => integer(r.customersNew) },
    { key: 'customersClosed', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', sortValue: (r) => r.customersClosed, cell: (r) => integer(r.customersClosed) },
    { key: 'closurePctNew', header: 'Closure % (New)', title: 'Customers · New ÷ Deals Assigned.', sortValue: (r) => r.closurePctNew, cell: (r) => percentage(r.closurePctNew) },
    { key: 'closurePctClosed', header: 'Closure % (Closed)', title: 'Customers · Closed ÷ Deals Assigned — can exceed 100% (closed deals may be created before the window).', sortValue: (r) => r.closurePctClosed, cell: (r) => percentage(r.closurePctClosed) },
  ]
  return (
    <Card>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<PercentIcon sx={{ fontSize: 18 }} />}>Sales POC &amp; pipeline-wise closure %</SectionTitle>
          {windowLabel && <ScopeTag label={`Selected: ${windowLabel}`} title="Follows the period filter above." />}
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
          Deals Assigned = marketing deals created in the window, by Sales POC across every pipeline. Click a name for the pipeline-wise breakdown.
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <ToggleBar open={detailsOpen} onClick={() => setDetailsOpen(!detailsOpen)}>
          <strong>{detailsOpen ? 'Hide Sales POC × pipeline table' : 'Show Sales POC × pipeline table'}</strong>
        </ToggleBar>
        {detailsOpen && (
          <Box sx={{ mt: 2 }}>
            <MfDataTable
              rows={rows}
              rowKey={(r) => r.owner}
              emptyText="Sales POCs appear after HubSpot sync."
              onRowClick={(r) => setOpenOwner(openOwner === r.owner ? null : r.owner)}
              isRowActive={(r) => openOwner === r.owner}
              columns={[
                { key: 'owner', header: 'Sales POC', title: 'Deal owner on HubSpot.', sortValue: (r) => r.owner, cell: (r) => <DrillName open={openOwner === r.owner} name={r.owner} team={r.team} /> },
                ...cols(false),
              ]}
              renderExpanded={(r) => (openOwner === r.owner ? (
                <Box sx={drillBox()}>
                  <MfDataTable
                    rows={r.pipelines}
                    rowKey={(p) => p.pipelineId}
                    columns={[
                      { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (p) => p.pipelineLabel, cell: (p) => <strong>{stripPipeline(p.pipelineLabel)}</strong> },
                      ...cols(true),
                    ]}
                  />
                </Box>
              ) : null)}
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}
